// AddToHomeScreenSheet.tsx — the full "Add ReNurture to your home screen" guide,
// shown as an overlay. Opened from the dismissible Today tile AND from Settings
// (so a user who missed the tile can always find it). Auto-detects the device:
//   - Android: a one-tap "Add to home screen" button when Chrome has offered it
//     (see services/installPrompt), plus the manual browser-menu steps as a fallback.
//   - iOS: Apple gives no install API, so we show the Share-sheet steps PLUS a short
//     how-to video (hosted on Bunny Stream).
//   - desktop / already installed / native app: a plain note (the Settings row that
//     opens this is itself hidden in those cases).
//
// House style: no emoji, no em-dash.
import React from 'react';
import { Capacitor } from '@capacitor/core';
import { Wordmark, Icon } from '@/components/icons';
import { Btn, Body } from '@/components/ui';
import { installPromptAvailable, onInstallPromptChange, triggerInstall } from '@/services/installPrompt';

// Bunny Stream library + the "add to home screen (iphone)" clip (portrait, 1080x2344).
const BUNNY_LIBRARY = '674762';
const IOS_VIDEO_GUID = '9363fff9-8f8d-493b-b6ad-c0b85ff76782';
const IOS_VIDEO_RATIO = 1080 / 2344;

type Platform = 'ios' | 'android' | 'other';

function detectPlatform(): Platform {
  if (typeof navigator === 'undefined') return 'other';
  const ua = navigator.userAgent || '';
  // iPadOS 13+ reports as a Mac; the touch-points check disambiguates a real iPad.
  const iOS = /iPad|iPhone|iPod/.test(ua) || (/Macintosh/.test(ua) && (navigator.maxTouchPoints || 0) > 1);
  if (iOS) return 'ios';
  if (/Android/.test(ua)) return 'android';
  return 'other';
}

function Step({ n, children }: { n: number; children: React.ReactNode }) {
  return (
    <li style={{ display: 'flex', gap: 11, alignItems: 'flex-start' }}>
      <span
        aria-hidden="true"
        style={{
          flexShrink: 0, width: 24, height: 24, borderRadius: 999, marginTop: 1,
          background: 'rgba(200,134,26,0.16)', color: 'var(--amber-deep)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontFamily: 'Inter, system-ui, sans-serif', fontSize: 13, fontWeight: 700,
        }}
      >
        {n}
      </span>
      <Body size={14} style={{ lineHeight: 1.55, paddingTop: 2 }}>{children}</Body>
    </li>
  );
}

export function AddToHomeScreenSheet({ onClose }: { onClose: () => void }) {
  const platform = React.useMemo(detectPlatform, []);
  const native = Capacitor.isNativePlatform();
  // Re-render when Chrome (Android) offers / consumes the install prompt.
  const [, force] = React.useReducer((x: number) => x + 1, 0);
  React.useEffect(() => onInstallPromptChange(force), []);
  const canOneTap = installPromptAvailable();

  const oneTap = async () => {
    const outcome = await triggerInstall();
    if (outcome === 'accepted') onClose();
  };

  return (
    <div
      role="dialog"
      aria-label="Add ReNurture to your home screen"
      style={{
        position: 'fixed', inset: 0, zIndex: 140, background: 'var(--cream)',
        display: 'flex', flexDirection: 'column', overflowY: 'auto',
        WebkitOverflowScrolling: 'touch', animation: 'rise .4s cubic-bezier(.2,.7,.3,1)',
      }}
    >
      <div
        style={{
          width: '100%', maxWidth: 520, margin: '0 auto', flex: 1,
          display: 'flex', flexDirection: 'column',
          padding: 'calc(20px + var(--safe-top)) 26px calc(30px + var(--safe-bottom))',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 }}>
          <Wordmark h={20} />
          <button
            type="button" onClick={onClose} aria-label="Close"
            style={{
              minWidth: 40, minHeight: 40, display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-soft)',
            }}
          >
            <Icon name="chevDown" size={22} />
          </button>
        </div>

        <h1 style={{ fontFamily: 'Fraunces, Georgia, serif', fontWeight: 400, fontSize: 26, lineHeight: 1.2, margin: '0 0 8px', color: 'var(--ink)' }}>
          Add ReNurture to your home screen
        </h1>
        <Body size={14.5} style={{ lineHeight: 1.6, color: 'var(--ink-soft)', marginBottom: 22 }}>
          It opens full screen from an icon, like an app, so it is one tap to come back to instead of typing the address each time.
        </Body>

        {native ? (
          <Body size={14}>You are already using the installed app.</Body>
        ) : platform === 'ios' ? (
          <>
            <ol style={{ listStyle: 'none', margin: '0 0 22px', padding: 0, display: 'flex', flexDirection: 'column', gap: 14 }}>
              <Step n={1}>Tap the <strong>Share</strong> button at the bottom of Safari (the square with an upward arrow).</Step>
              <Step n={2}>Scroll down and choose <strong>Add to Home Screen</strong>.</Step>
              <Step n={3}>Tap <strong>Add</strong> in the top corner. The ReNurture icon appears on your home screen.</Step>
            </ol>
            <div style={{ position: 'relative', width: '100%', maxWidth: 280, margin: '0 auto', aspectRatio: String(IOS_VIDEO_RATIO), maxHeight: '60vh', borderRadius: 18, overflow: 'hidden', boxShadow: '0 18px 50px rgba(0,0,0,0.18)', background: '#000' }}>
              <iframe
                title="How to add ReNurture to your iPhone home screen"
                src={`https://iframe.mediadelivery.net/embed/${BUNNY_LIBRARY}/${IOS_VIDEO_GUID}?autoplay=false&preload=true&responsive=true`}
                loading="lazy"
                allow="fullscreen; picture-in-picture; encrypted-media"
                allowFullScreen
                style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 0 }}
              />
            </div>
          </>
        ) : platform === 'android' ? (
          <>
            {canOneTap ? (
              <>
                <Body size={14} style={{ lineHeight: 1.6, marginBottom: 16 }}>
                  Your browser can do this in one tap.
                </Body>
                <Btn size="lg" onClick={() => void oneTap()} ariaLabel="Add ReNurture to your home screen" style={{ alignSelf: 'flex-start' }}>
                  Add to home screen
                </Btn>
              </>
            ) : (
              <ol style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 14 }}>
                <Step n={1}>Tap the <strong>menu</strong> (the three dots) in the top corner of your browser.</Step>
                <Step n={2}>Choose <strong>Add to Home screen</strong> (it may say <strong>Install app</strong>).</Step>
                <Step n={3}>Tap <strong>Add</strong>. The ReNurture icon appears on your home screen.</Step>
              </ol>
            )}
          </>
        ) : (
          <Body size={14} style={{ lineHeight: 1.6 }}>
            Open ReNurture on your phone to add it to your home screen. On a computer, you can keep it bookmarked or pinned as a tab.
          </Body>
        )}
      </div>
    </div>
  );
}
